import React, { useState } from 'react';
import { sheetsClient } from '../../data/sheetsClient';
import { useTeacherData } from '../../context/TeacherDataContext';
import { Card, Button, OutstandingBanner, Modal, AnimatePresence, Skeleton, EmptyState } from '../shared/UI';
import Badge from '../shared/Badge';
import { schoolFeeSummary, entryRateFor, formatCurrency } from '../../utils/feeCalculations';
import { schoolCreditBalance } from '../../../shared/utils/feePaymentLedger';

export default function Invoices({ navigateTo }) {
  const { dancers, entries, feeConfig, invoiceSummary, locks, entryFeePayments, loading, refetch, staff, timeline, event } = useTeacherData();
  const eventName = event?.[0]?.eventName || 'OSS Dancefest';
  const [participantsModalOpen, setParticipantsModalOpen] = useState(false);
  const [requestingParticipants, setRequestingParticipants] = useState(false);
  const [entriesModalOpen, setEntriesModalOpen] = useState(false);
  const [requestingEntries, setRequestingEntries] = useState(false);
  const [entryBreakdownOpen, setEntryBreakdownOpen] = useState(false);

  if (loading.dancers || loading.entries || loading.feeConfig || loading.invoiceSummary || loading.locks || loading.staff || loading.timeline || loading.entryFeePayments) {
    return (
      <div style={{ padding: 20, margin: '0 auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Invoices & payments</h1>
        <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>{eventName} · school invoice overview</p>
        <Card title="Fee summary">
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #E5E5E5' }}>
              <Skeleton width="45%" height={14} />
              <Skeleton width={90} height={22} radius={20} />
            </div>
          ))}
        </Card>
        <Card>
          <Skeleton width="35%" height={16} />
        </Card>
      </div>
    );
  }

  // The "Event Registration" timeline row already carries the real signal
  // for whether this school has actually submitted registration this year
  // (see backend's getSchoolRegistrationStatus) — reuse it here instead of
  // showing fees/invoices as due before there's any registration behind them.
  const eventRegRow = (timeline || []).find((t) => t.title === 'Event Registration');
  const isEventRegistered = !eventRegRow || eventRegRow.status !== 'not-registered';

  const summary = schoolFeeSummary(dancers, entries, feeConfig, invoiceSummary.schoolRegistrationFee, staff.length, entryFeePayments);
  // Money owed back to the school from a deleted paid participant/entry (or
  // an entry left behind during a dancer replacement) — see
  // schoolCreditBalance's own comment. Read-only here; an admin applies it
  // toward a fee the same way they record any other payment.
  const creditBalance = schoolCreditBalance(entryFeePayments);

  const handleRequestSchoolInvoice = () => {
    sheetsClient.openInvoicePdf('school');
  };

  const handleRequestParticipantsInvoice = async () => {
    setRequestingParticipants(true);
    try {
      await sheetsClient.lockParticipants();
      await refetch.locks();
      sheetsClient.openInvoicePdf('participants');
      setParticipantsModalOpen(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setRequestingParticipants(false);
    }
  };

  const handleRequestEntriesInvoice = async () => {
    setRequestingEntries(true);
    try {
      await sheetsClient.lockEntries();
      await refetch.locks();
      sheetsClient.openInvoicePdf('entries');
      setEntriesModalOpen(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setRequestingEntries(false);
    }
  };

  // Re-opens a previously requested invoice's PDF — the endpoint regenerates
  // it live from current data, so it's safe to hit again any time (e.g. if
  // the teacher lost track of where the first download landed).
  const handleDownloadParticipantsInvoice = () => {
    sheetsClient.openInvoicePdf('participants');
  };

  const handleDownloadEntriesInvoice = () => {
    sheetsClient.openInvoicePdf('entries');
  };

  return (
    <div style={{ padding: 20, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Invoices & payments</h1>
      <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>{eventName} · school invoice overview</p>

      {!isEventRegistered ? (
        <Card>
          <EmptyState
            icon="🧾"
            title="No invoices yet"
            description="Your school hasn't completed event registration yet — fees and invoices will appear here once you do."
            action={<Button variant="primary" onClick={() => navigateTo('event-registration')}>Complete event registration</Button>}
          />
        </Card>
      ) : (
        <>
      {(() => {
        const outstandingBannerItems = [
          summary.schoolRegistrationRemaining > 0 && { label: 'School registration fee remaining' },
          summary.participationRemaining > 0 && { label: 'Participation fee remaining', meta: `Due ${invoiceSummary.participationFeeDueDate}` },
          summary.entryFeeRemaining > 0 && { label: 'Entry fees remaining', meta: `Due ${invoiceSummary.entryFeeDueDate}` },
          summary.additionalStaffFeeRemaining > 0 && { label: 'Additional teacher/chaperone fee remaining' },
        ].filter(Boolean);
        return outstandingBannerItems.length > 0 && (
          <OutstandingBanner
            id="invoices-outstanding"
            title={`Outstanding balance: ${formatCurrency(summary.totalOutstanding)}`}
            items={outstandingBannerItems}
          />
        );
      })()}

      <Card title="Fee summary">
        <LineItem
          label="School registration fee"
          rightBadge={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <RequestLink
                onClick={handleRequestSchoolInvoice}
                label={invoiceSummary.schoolRegistrationFee.paid ? 'Download invoice' : 'Request invoice'}
              />
              <Badge variant={invoiceSummary.schoolRegistrationFee.paid ? 'success' : 'danger'}>
                {invoiceSummary.schoolRegistrationFee.paid ? 'Paid' : 'Due'} · {formatCurrency(invoiceSummary.schoolRegistrationFee.amount)}
              </Badge>
            </div>
          }
          struckThrough={invoiceSummary.schoolRegistrationFee.paid}
        />
        <LineItem
          label={`Participation fee — ${summary.participationPaidCount} paid, ${summary.totalDancers - summary.participationPaidCount} remaining`}
          rightBadge={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {!locks.participantsLocked && summary.totalDancers > 0 && summary.participationRemaining > 0 && (
                <RequestLink onClick={() => setParticipantsModalOpen(true)} />
              )}
              {locks.participantsLocked && (
                <RequestLink onClick={handleDownloadParticipantsInvoice} label="Download invoice" />
              )}
              <Badge variant={summary.participationRemaining > 0 ? 'danger' : 'success'}>{summary.participationRemaining > 0 ? `Due · ${formatCurrency(summary.participationRemaining)}` : 'Paid'}</Badge>
            </div>
          }
        />
        <div style={{ borderBottom: '1px solid #E5E5E5' }}>
          <div
            onClick={() => setEntryBreakdownOpen((o) => !o)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', fontSize: 13, cursor: 'pointer' }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: '#999', width: 10, display: 'inline-block' }}>{entryBreakdownOpen ? '▾' : '▸'}</span>
              Entry fees
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} onClick={(e) => e.stopPropagation()}>
              {!locks.entriesLocked && entries.length > 0 && summary.entryFeeRemaining > 0 && (
                <RequestLink onClick={() => setEntriesModalOpen(true)} />
              )}
              {locks.entriesLocked && entries.length > 0 && (
                <RequestLink onClick={handleDownloadEntriesInvoice} label="Download invoice" />
              )}
              <Badge variant={entries.length === 0 ? 'neutral' : summary.entryFeeRemaining > 0 ? 'danger' : 'success'}>
                {entries.length === 0 ? 'No entries yet' : summary.entryFeeRemaining > 0 ? `Due · ${formatCurrency(summary.entryFeeRemaining)}` : 'Paid'}
              </Badge>
            </div>
          </div>
          {entryBreakdownOpen && (
            <div style={{ paddingLeft: 18, paddingBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <Button size="sm" onClick={() => sheetsClient.openInvoicePdf('entries')}>↓ Download current snapshot (PDF)</Button>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <Th>Entry</Th>
                    <Th align="right"># Dancers</Th>
                    <Th align="right">Cost / dancer</Th>
                    <Th align="right">Total</Th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr><Td colSpan={4} style={{ color: '#666' }}>No entries yet — add entries from the Entry Library to see costs here.</Td></tr>
                  ) : (
                    entries.map((e) => {
                      const numDancers = (e.dancerIds || []).length;
                      const costPerDancer = entryRateFor(e, feeConfig.ENTRY_RATES);
                      return (
                        <tr key={e.id}>
                          <Td>{e.title}</Td>
                          <Td align="right">{numDancers}</Td>
                          <Td align="right">{formatCurrency(costPerDancer)}</Td>
                          <Td align="right">{formatCurrency(costPerDancer * numDancers)}</Td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, marginTop: 6, borderTop: '1px solid #D5D5D5', fontSize: 14, fontWeight: 500 }}>
                <span>Total entry fees</span>
                <span>{formatCurrency(summary.entryFeeTotal)}</span>
              </div>
            </div>
          )}
        </div>
        {summary.additionalStaffFeeRemaining > 0 && (
          <LineItem
            label={`Additional teacher/chaperone fee — ${staff.length - 2} beyond the 2 included`}
            rightBadge={<Badge variant="danger">{`Due · ${formatCurrency(summary.additionalStaffFeeRemaining)}`}</Badge>}
          />
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, marginTop: 6, borderTop: '1px solid #D5D5D5', fontSize: 15, fontWeight: 500 }}>
          <span>Outstanding</span>
          <span style={{ color: '#A32D2D' }}>{formatCurrency(summary.totalOutstanding)}</span>
        </div>
        {creditBalance > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, fontSize: 13 }}>
            <span style={{ color: '#666' }}>School credit available</span>
            <span style={{ color: '#0F6E56', fontWeight: 500 }}>{formatCurrency(creditBalance)}</span>
          </div>
        )}
      </Card>
        </>
      )}

      <AnimatePresence>
        {participantsModalOpen && (
          <ConfirmInvoiceModal
            key="participants-modal"
            title="Participants invoice"
            checkboxLabel="I confirm all dancer registrations are complete and final for this event"
            warningText="This closes the self-registration link — no new dancers can sign up afterward."
            confirmLabel="Request participants invoice"
            confirming={requestingParticipants}
            onConfirm={handleRequestParticipantsInvoice}
            onClose={() => setParticipantsModalOpen(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {entriesModalOpen && (
          <ConfirmInvoiceModal
            key="entries-modal"
            title="Entries invoice"
            checkboxLabel="I confirm all dance entries are complete and final for this event"
            warningText="This closes the Entry Library to new entries — existing entries can still swap dancers, but not exceed their current headcount."
            confirmLabel="Request entries invoice"
            confirming={requestingEntries}
            onConfirm={handleRequestEntriesInvoice}
            onClose={() => setEntriesModalOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function ConfirmInvoiceModal({ title, checkboxLabel, warningText, confirmLabel, confirming, onConfirm, onClose }) {
  const [checked, setChecked] = useState(false);
  return (
    <Modal onClose={onClose} panelStyle={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 16, fontWeight: 500 }}>{title}</span>
        <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: '#666' }}>✕</button>
      </div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12, fontSize: 13, cursor: 'pointer' }}>
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} style={{ accentColor: '#ED1C24', width: 16, height: 16, marginTop: 1, flexShrink: 0 }} />
        {checkboxLabel}
      </label>
      <span style={{ display: 'block', fontSize: 12, color: '#888', marginBottom: 18 }}>{warningText}</span>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Button size="sm" onClick={onClose}>Cancel</Button>
        <Button variant="primary" size="sm" disabled={!checked || confirming} onClick={onConfirm}>
          {confirming ? 'Requesting…' : confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

function LineItem({ label, rightBadge, struckThrough }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #E5E5E5', fontSize: 13, color: struckThrough ? '#999' : '#1A1A1A' }}>
      <span>{label}</span>
      {rightBadge}
    </div>
  );
}

function RequestLink({ onClick, disabled, label = 'Request invoice' }) {
  const style = { fontSize: 12, color: '#2D2E33', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1 };
  return <button type="button" onClick={onClick} disabled={disabled} style={style}>{label}</button>;
}

function Th({ children, align }) {
  return <th style={{ textAlign: align || 'left', fontSize: 12, color: '#666', fontWeight: 500, padding: '6px', borderBottom: '1px solid #E5E5E5' }}>{children}</th>;
}
function Td({ children, align, bold, color, colSpan }) {
  return <td colSpan={colSpan} style={{ fontSize: 13, padding: '8px 6px', borderBottom: '1px solid #E5E5E5', textAlign: align || 'left', fontWeight: bold ? 500 : 400, color }}>{children}</td>;
}
